import { useState } from 'react';
import { Stack, InputAdornment, IconButton, Link, Typography, useTheme } from '@mui/material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate } from 'react-router';
import { useDispatch } from 'react-redux';
import { LoadingButton } from '@mui/lab';

import { useRequestState } from '@app/hooks';
import { FormProvider, Iconify, RHFTextField } from '@app/components';
import { Strings } from '@app/constants';
import { PATH_AUTH } from '@app/routes/paths';
import { SvgColor } from '@app/components/svg-color';
import { typography } from '@app/theme/typography';
import { login } from '../slice';
import type { ILoginSchema } from '../types';
import { loginSchema } from '../validators';
import { selectLoginState } from '../selectors';

// --------------------------------------------

const LoginForm = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const dispatch = useDispatch();

  const onClickEyeIcon = () => {
    setShowPassword(!showPassword);
  };

  const methods = useForm<ILoginSchema>({
    resolver: yupResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = (form: ILoginSchema) => {
    dispatch(login(form));
  };

  const onClickForgotPassword = () => {
    const email = methods.getValues('email');
    navigate(PATH_AUTH.forgotPassword, { state: { email } });
  };
  const { loading } = useRequestState({
    stateSelector: selectLoginState,
    errorShown: true,
    successMessageShown: true,
  });

  return (
    <FormProvider methods={methods} onSubmit={methods.handleSubmit(onSubmit)}>
      <Stack>
        <Typography
          fontFamily={typography.fontFamily}
          fontSize={{ xs: '12px', md: '15px' }}
          fontWeight={typography.fontWeightRegular}
        >
          {Strings.field.emailAddress}
        </Typography>
        <RHFTextField
          data-testId="login.email"
          name="email"
          type="email"
          fullWidth
          size="small"
          sx={{ marginBottom: 2, backgroundColor: theme.palette.grey[50], borderRadius: '10px' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SvgColor fontSize={'10px'} src={`/assets/icons/ic_email.svg`} />
              </InputAdornment>
            ),
          }}
        />
        <Typography
          fontFamily={typography.fontFamily}
          fontSize={{ xs: '12px', md: '15px' }}
          fontWeight={typography.fontWeightRegular}
        >
          {Strings.field.password}
        </Typography>
        <RHFTextField
          data-testId="login.password"
          name="password"
          fullWidth
          size="small"
          sx={{ marginBottom: 2, backgroundColor: theme.palette.grey[50], borderRadius: '10px' }}
          type={showPassword ? 'text' : 'password'}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SvgColor fontSize={'10px'} src={`/assets/icons/ic_password.svg`} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={onClickEyeIcon}>
                  {showPassword ? <Iconify icon="mdi:eye" /> : <Iconify icon="mdi:eye-off" />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Link
          variant="body2"
          color="inherit"
          // href={PATH_AUTH.forgotPassword}
          sx={{ marginLeft: 'auto !important', mt: '4px', fontFamily: typography.fontFamily }}
          // onClick={onClickForgotPassword}
        >
          {Strings.auth.forgotPassword}
        </Link>

        <LoadingButton
          data-testId="login.submit"
          size="large"
          variant="contained"
          type="submit"
          loading={loading}
          sx={{
            marginTop: { xs: 5, md: 10 },
            borderRadius: '50px',
          }}
        >
          {Strings.button.signIn}
        </LoadingButton>
        <Typography variant="subtitle2" alignSelf={'center'} mt={5}>
          {Strings.common.dontHaveAccount}
          <Link
            variant="body2"
            color="inherit"
            href={PATH_AUTH.signUp}
            sx={{ color: theme.palette.primary.dark, fontWeight: 'bold', ml: 0.5 }}
          >
            {Strings.auth.signUp}
          </Link>
        </Typography>
      </Stack>
    </FormProvider>
  );
};

export { LoginForm };
